import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { PedidoService } from './pedido.service';
import { NotificacaoFcmService } from '../notificacao/notificacao-fcm.service';
import { AuthService } from '../auth.service';
import {
  NotificacaoPedidoDTO,
  TipoNotificacaoPedido,
} from '../../models/pedidos/notificacao-pedido.model';

/**
 * Resumo de um pedido pendente (estado ABERTO) para exibicao no popover
 * do header.
 */
export interface PedidoPendenteResumo {
  id: number;
  codigo: string;
  nomeCliente: string;
  criadoEm: string;
  /** RETIRADA_NO_LOCAL | DELIVERY | undefined quando ausente/desconhecido */
  tipoEntrega?: string;
}

/**
 * Mantem a contagem e a lista de pedidos em estado ABERTO (novos, ainda
 * nao vistos). Usado pelo badge do header para persistir a visibilidade
 * dos pedidos mesmo quando a notificacao toast ja desapareceu.
 *
 * Fontes de atualizacao:
 *  1) Carga inicial via API ao autenticar.
 *  2) Notificacoes FCM em tempo real — incrementa em NOVO_PEDIDO e
 *     decrementa quando um pedido sai de ABERTO.
 */
@Injectable({ providedIn: 'root' })
export class PedidosPendentesService implements OnDestroy {

  private readonly pedidosSubject = new BehaviorSubject<PedidoPendenteResumo[]>([]);

  /** Lista atual de pedidos abertos (mais recentes primeiro). */
  readonly pedidos$ = this.pedidosSubject.asObservable();

  /**
   * Emite o ID do pedido que deve ser aberto no dialog de detalhe.
   * Usado para comunicar ao PedidosComponent qual pedido exibir quando
   * o usuario clica "Ver" numa notificacao FCM enquanto ja esta em /pedidos
   * (evita depender de history.state em same-URL navigation).
   */
  private readonly abrirPedidoSubject = new Subject<number>();
  readonly abrirPedido$: Observable<number> = this.abrirPedidoSubject.asObservable();

  /** Solicita abertura do dialog de detalhe para um pedido especifico. */
  solicitarAberturaPedido(pedidoId: number): void {
    this.abrirPedidoSubject.next(pedidoId);
  }

  private subs = new Subscription();

  constructor(
    private pedidoService: PedidoService,
    private fcmService: NotificacaoFcmService,
    private authService: AuthService,
  ) {
    // Recarrega a lista sempre que o usuario autentica; limpa no logout.
    this.subs.add(
      this.authService.isAuthenticated$.subscribe(autenticado => {
        if (autenticado) {
          this.recarregar();
        } else {
          this.pedidosSubject.next([]);
        }
      }),
    );

    // Mantem o contador sincronizado com o websocket/FCM.
    this.subs.add(
      this.fcmService.notificacoes$.subscribe(n => this.onNotificacao(n)),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Quantidade atual de pedidos abertos. */
  get total(): number {
    return this.pedidosSubject.value.length;
  }

  /** Busca do backend a lista atual de pedidos abertos. */
  recarregar(): void {
    const filtro = { estadoDePedido: 'ABERTO', size: 50, page: 0 };
    this.pedidoService.get('/pedidos', filtro).subscribe({
      next: (resp: any) => {
        const content = (resp?.content || []) as any[];
        const lista: PedidoPendenteResumo[] = content.map(p => ({
          id: p.id,
          codigo: p.codigoDePedido || p.codigo || String(p.id),
          nomeCliente: p.nomeCliente || p.cliente?.nome || '',
          criadoEm: p.criadoEm || p.dataInclusao || '',
          tipoEntrega: this.resolverTipoEntrega(p.tipoEntrega),
        }));
        this.pedidosSubject.next(lista);
      },
      error: () => {
        // Silencioso: se a API falhar, mantemos a lista atual.
      },
    });
  }

  /** Resolve tipoEntrega que pode vir como string ou LocalizableEnum { enum, nome } */
  private resolverTipoEntrega(tipo: any): string | undefined {
    if (!tipo) return undefined;
    if (typeof tipo === 'string') return tipo;
    if (tipo?.enum) return tipo.enum;
    if (tipo?.name) return tipo.name;
    return undefined;
  }

  /** Remove um pedido da lista (ex.: ao clicar em "Ver pedido"). */
  marcarComoVisto(pedidoId: number): void {
    const atual = this.pedidosSubject.value;
    const nova = atual.filter(p => p.id !== pedidoId);
    if (nova.length !== atual.length) {
      this.pedidosSubject.next(nova);
    }
  }

  /** Limpa todos os pedidos pendentes (ex.: ao navegar para /pedidos). */
  limparTodos(): void {
    if (this.pedidosSubject.value.length > 0) {
      this.pedidosSubject.next([]);
    }
  }

  private onNotificacao(n: NotificacaoPedidoDTO): void {
    if (n.tipo === TipoNotificacaoPedido.NOVO_PEDIDO) {
      // Evita duplicar se o pedido ja existir (p.ex. recarga + push quase simultaneos).
      const jaExiste = this.pedidosSubject.value.some(p => p.id === n.pedidoId);
      if (jaExiste) return;

      const novo: PedidoPendenteResumo = {
        id: n.pedidoId,
        codigo: n.codigoDePedido,
        nomeCliente: n.nomeCliente,
        criadoEm: n.timestamp,
        tipoEntrega: n.tipoEntrega,
      };
      this.pedidosSubject.next([novo, ...this.pedidosSubject.value]);
      // Se o push nao trouxe tipoEntrega, recarrega para preencher o badge do popover.
      if (!n.tipoEntrega) {
        this.recarregar();
      }
      return;
    }

    if (n.tipo === TipoNotificacaoPedido.MUDANCA_ESTADO && n.estadoAnterior === 'ABERTO') {
      // Saiu do estado ABERTO (producao, cancelado, etc.) — deixou de ser pendente.
      this.marcarComoVisto(n.pedidoId);
    }
  }
}
